import { get, set } from "lodash";
import { batch } from "react-redux";

import validationSchemas from "../../../../../utils/validationSchemas";

import { getActionBaseProvider, getActionProvider } from "../../../../Application/actions/actionsBuilder";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { actionTypes } from "../actionTypes/assessmentContentValidatorActionTypes";
import { publishDraftAssessmentEntity } from "./assessmentEntityStateActions";
import { getAllErrorsLeafs } from "../../../Common/utils/validationUtils";
import { assessmentsStateSelector } from "../selectors";
import { type NotifyStepSettings } from "../../../../../interfaces";

export const setIsValidAction = getActionProvider<boolean>(actionTypes.setIsValid);
export const setErrorsResolvedAction = getActionProvider<boolean>(actionTypes.setResolved);
export const setActiveErrorsAction = getActionProvider<object>(actionTypes.setActiveErrors);
export const setTouchedSnapshotAction = getActionProvider<object>(actionTypes.setTouchedSnapshot);
export const setErrorModeAction = getActionProvider<boolean>(actionTypes.setErrorMode);
export const setInitializedAction = getActionProvider<boolean>(actionTypes.setInitialized);
export const resetAction = getActionBaseProvider(actionTypes.reset);

const getValidationState = (getState: () => RootState) =>
  assessmentsStateSelector(getState()).base.assessmentContentValidatorReducer;

const turnErrorMode = (on: boolean) => (dispatch: AppDispatch) => {
  batch(() => {
    dispatch(setErrorModeAction(on));
    dispatch(setErrorsResolvedAction(!on));
  });
};

export const tryPublish =
  (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const assessment = assessmentsStateSelector(getState()).base.assessmentDetailsReducer;
    const assessmentValidation = getValidationState(getState);

    if (assessmentValidation.initialized && !assessmentValidation.isFormValid) {
      dispatch(turnErrorMode(true));
      return;
    }

    if (!assessmentValidation.initialized) {
      try {
        await validationSchemas.assessmentContent.validate(assessment.content, { abortEarly: true });
      } catch {
        dispatch(turnErrorMode(true));
        return;
      }
    }

    batch(() => {
      dispatch(publishDraftAssessmentEntity(id, notificationSettings, notifyTypes));
      dispatch(resetAction());
    });
  };

export const setActiveErrors = (errors: object) => (dispatch: AppDispatch) => {
  let activeErrors: { [key: string]: string } = {};
  let touched: object = {};
  getAllErrorsLeafs(errors, (key, value) => {
    activeErrors[key] = value;
    set(touched, key, true);
  });

  batch(() => {
    dispatch(setTouchedSnapshotAction(touched));
    dispatch(setActiveErrorsAction(activeErrors));
  });
};

export const checkForResolvedErrors = (errors: object) => (dispatch: AppDispatch, getState: () => RootState) => {
  const assessmentValidation = getValidationState(getState);

  for (let item in assessmentValidation.activeErrors) {
    if (get(errors, item)) {
      return;
    }
  }

  batch(() => {
    dispatch(setErrorsResolvedAction(true));
    dispatch(setTouchedSnapshotAction({}));
    dispatch(setActiveErrorsAction({}));
  });
};
